/* Two-step EU withdrawal form (sections/eu-withdrawal-form.liquid).
   Step 1 collects the submission, step 2 shows a read-only review with the
   "Confirm withdrawal" submit. On confirm the submission is stamped and kept
   in sessionStorage; after Shopify re-renders the page with the success state,
   the stored submission is replayed to Klaviyo as an "EU Withdrawal Submitted"
   event so the metric-triggered flow can email the acknowledgement of receipt.
   The event fires only after a confirmed POST so no acknowledgement is sent
   for submissions Shopify rejected (validation errors, captcha abandonment). */

if (!customElements.get('withdrawal-form')) {
  const STORAGE_KEY = 'lj_withdrawal_ack';
  const METRIC_NAME = 'EU Withdrawal Submitted';

  class WithdrawalForm extends HTMLElement {
    connectedCallback() {
      this.ackStatus = this.querySelector('[data-ack-status]');
      if (this.ackStatus) {
        this.handleSuccess();
        return;
      }

      this.form = this.querySelector('form');
      if (!this.form) return;

      this.classList.add('is-enhanced');
      this.steps = Array.from(this.querySelectorAll('[data-step]'));

      const nextButton = this.querySelector('[data-step-next]');
      if (nextButton) nextButton.addEventListener('click', () => this.goToReview());

      const backButton = this.querySelector('[data-step-back]');
      if (backButton) backButton.addEventListener('click', () => this.showStep(1));

      this.form.addEventListener('submit', () => this.stampAndStore());

      this.prefillOrderNumber();
      this.showStep(1);
    }

    prefillOrderNumber() {
      const input = this.querySelector('#WithdrawalForm-order');
      if (!input || input.value) return;
      const order = new URLSearchParams(window.location.search).get('order');
      if (order) input.value = order;
    }

    showStep(number) {
      this.steps.forEach((step) => {
        step.hidden = Number(step.dataset.step) !== number;
      });
      if (number === 2) {
        const heading = this.querySelector('[data-step-heading]');
        if (heading) heading.focus();
      }
    }

    goToReview() {
      const controls = this.querySelectorAll(
        '[data-step="1"] input, [data-step="1"] select, [data-step="1"] textarea'
      );
      for (const control of controls) {
        if (!control.reportValidity()) return;
      }
      this.populateSummary();
      this.showStep(2);
    }

    collectFields() {
      const fields = [];
      this.querySelectorAll('[data-step="1"] .form-field').forEach((wrapper) => {
        const label = wrapper.querySelector('.form-field__label');
        const control = wrapper.querySelector('input, select, textarea');
        if (!label || !control || !control.value) return;
        fields.push({
          label: label.textContent.replace('*', '').trim(),
          value: control.value,
        });
      });
      return fields;
    }

    populateSummary() {
      const summary = this.querySelector('[data-summary]');
      if (!summary) return;
      summary.innerHTML = '';
      this.collectFields().forEach(({ label, value }) => {
        const dt = document.createElement('dt');
        dt.textContent = label;
        const dd = document.createElement('dd');
        dd.textContent = value;
        summary.append(dt, dd);
      });
    }

    stampAndStore() {
      const submittedAt = new Date().toISOString();
      const stamp = this.querySelector('[data-submitted-at]');
      if (stamp) stamp.value = submittedAt;

      const email = this.querySelector('input[name="contact[email]"]');
      const statement = this.querySelector('input[name="contact[Statement]"]');
      const payload = {
        submittedAt,
        email: email ? email.value : '',
        statement: statement ? statement.value : '',
        fields: this.collectFields(),
      };

      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
      } catch (error) {
        // Storage unavailable (e.g. blocked); the store notification still records the submission.
      }
    }

    handleSuccess() {
      let stored = null;
      try {
        stored = JSON.parse(sessionStorage.getItem(STORAGE_KEY));
      } catch (error) {
        stored = null;
      }
      if (!stored || !stored.email) return;

      this.renderReceipt(stored);
      this.sendAcknowledgement(stored);
    }

    renderReceipt(stored) {
      const receipt = this.querySelector('[data-receipt]');
      const list = this.querySelector('[data-receipt-list]');
      if (!receipt || !list) return;

      list.innerHTML = '';
      const rows = stored.fields.concat([
        {
          label: receipt.dataset.submittedAtLabel || 'Submitted at',
          value: new Date(stored.submittedAt).toLocaleString(),
        },
      ]);
      rows.forEach(({ label, value }) => {
        const dt = document.createElement('dt');
        dt.textContent = label;
        const dd = document.createElement('dd');
        dd.textContent = value;
        list.append(dt, dd);
      });
      receipt.hidden = false;

      const printButton = this.querySelector('[data-print]');
      if (printButton) printButton.addEventListener('click', () => window.print());
    }

    async sendAcknowledgement(stored) {
      const companyId = this.dataset.companyId;
      if (!companyId) return;

      const properties = {
        statement: stored.statement,
        submitted_at: stored.submittedAt,
      };
      stored.fields.forEach(({ label, value }) => {
        properties[label] = value;
      });

      const body = JSON.stringify({
        data: {
          type: 'event',
          attributes: {
            properties,
            time: stored.submittedAt,
            metric: { data: { type: 'metric', attributes: { name: METRIC_NAME } } },
            profile: { data: { type: 'profile', attributes: { email: stored.email } } },
          },
        },
      });

      const send = () =>
        fetch(`https://a.klaviyo.com/client/events/?company_id=${encodeURIComponent(companyId)}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
          body,
        });

      let delivered = false;
      try {
        let response = await send();
        if (!response.ok) {
          await new Promise((resolve) => setTimeout(resolve, 2000));
          response = await send();
        }
        delivered = response.ok;
      } catch (error) {
        delivered = false;
      }

      if (delivered) {
        try {
          sessionStorage.removeItem(STORAGE_KEY);
        } catch (error) {
          // ignore
        }
      }
      this.setAckStatus(delivered ? this.dataset.ackSent : this.dataset.ackFailed);
    }

    setAckStatus(message) {
      if (!this.ackStatus || !message) return;
      this.ackStatus.textContent = message;
      this.ackStatus.hidden = false;
    }
  }

  customElements.define('withdrawal-form', WithdrawalForm);
}
